import {Observable} from 'rxjs';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {APIResponse} from '../system/system-common';
import {SnapshotDto} from '../common/snapshot';
import {PagingResponse} from '../system/system-common';
import {Report} from '../common/pages';
import {WorkflowInfo} from '@core/interfaces/common/users';

export interface ReportLink {
    link: string;
}

export class ReportRequest {
    reportId: string;
    templateFlag: boolean;
    studyId: string;
    userId: string;
    sensitivityFlag: boolean;

    constructor(report: Report, studyId: string, userId: string, sensitivityFlag: boolean) {
        this.reportId = report ? report.reportId : '';
        this.templateFlag = report ? report.templateFlag : true;
        this.studyId = studyId;
        this.userId = userId;
        this.sensitivityFlag = sensitivityFlag;
    }
}

export interface OutputReportSource {
    links: [string];
}

export interface Workflow {
    id: number;
    createdOn: string;
    updatedOn: string;
    name: string;
    description: string;
    snapshotId: number;
    workflowType: WorkflowType;
    snapshotVersion: number;
    status: string;
    startTime: string;
    endTime: string;
    tag: string;
    note: string;
    createdBy: string;
    createdByName: string;
    workflowItems?: WorkflowItem[];
    duration: string;
}
export enum WorkflowType {
    STUDY = 'STUDY',
    LOAD_FORECAST = 'LOAD_FORECAST',
    CURVE_CALIBRATION = 'CURVE_CALIBRATION',
    CONNECTIVITY_ANALYSIS = 'CONNECTIVITY_ANALYSIS',
}

export interface WorkflowItemParam {
    id: number;
    createdOn: string;
    updatedOn?: any;
    workflowItemId: number;
    paramKey: string;
    value: string;
}

export interface WorkflowItem {
    id: number;
    createdOn: string;
    updatedOn?: any;
    itemName: string;
    sensitivityCode: string;
    status: string;
    defaultItem: boolean;
    executionOrder: number;
    startTime?: any;
    endTime?: any;
    currentYear: number;
    evaluationPeriod: number;
    workflowId: number;
    itemParams: WorkflowItemParam[];
}

export interface EventStatusResponse {
    status: 'success' | 'in_progress' | 'failed';
    message: string;
    eventId: number;
    // When status = 'in_progress'
    progressPercent?: number;
    progressPhase?: string;
    // When status = 'success'
    workflowId?: number;
    activeWorkflows?: WorkflowInfo[];
}

export interface ActivationPollingState {
    eventId: number;
    isPolling: boolean;
    lastStatus?: EventStatusResponse;
}

export enum WorkflowActivateStatusType {
    WORKFLOW_ACTIVE = 'WORKFLOW_ACTIVE',
    WORKFLOW_DELETED = 'WORKFLOW_DELETED',
    LOADING = 'LOADING',
}

export interface WorkflowActivateDto {
    workflowActivateStatus: WorkflowActivateStatusType;
    message: string;
    activateEventId: number;
    activeWorkflows?: WorkflowInfo[];
}

export abstract class WorkflowService {
    abstract getWorkflowOverviewDataSource(activeWorkflowIds: number[]): Observable<DataSource>;

    abstract getWorkflowDataSource(type: WorkflowType): Observable<DataSource>;

    abstract getWorkflowById(workflowId: number): Observable<APIResponse<Workflow>>;

    abstract getWorkflowItemById(workflowItemId: number): Observable<APIResponse<WorkflowItem>>;

    abstract updateWorkflowNoteTag(workflowId: number, Workflow): Observable<APIResponse<Workflow>>;

    abstract executeNewWorkflowFromSnapshot(
        snapshotId: number,
        workflowType: string,
        automated: boolean,
        date?: string,
    ): Observable<any>;

    abstract executeWorkflowWithoutSnapshot(param: any): Observable<APIResponse<Workflow>>;

    abstract findAllBySnapshotId(snapshotId: number): Observable<APIResponse<Array<Workflow>>>;

    abstract findBySnapshotDtoByWorkflowId(workflowId: number): Observable<APIResponse<SnapshotDto>>;

    abstract delete(workflowType: string, workflowId: number): Observable<APIResponse<boolean>>;

    abstract reversePendingDelete(workflowId: number): Observable<APIResponse<boolean>>;

    abstract clearActiveWorkflowsByUser(userId: string): Observable<APIResponse<boolean>>;

    abstract activateWorkflowsByUser(userId, workflowIds: number[]): Observable<APIResponse<WorkflowInfo[]>>;

    abstract activateWorkflowsByUserAsync(
        userId: string,
        workflowIds: number[],
    ): Observable<APIResponse<WorkflowActivateDto>>;

    abstract checkEventStatus(eventId: number): Observable<APIResponse<EventStatusResponse>>;

    abstract listLatestWorkflows(pageSize: number, workflowType: string): Observable<PagingResponse<Workflow>>;
}
